<template>
  <Page>
    <template #header>Liste de courses</template>
    <template #body>
      <div class="add-row">
        <ion-input
          v-model="newItem"
          placeholder="Ajouter un article..."
          @keyup.enter="addItem"
          class="add-input"
        ></ion-input>
        <ion-button @click="addItem" fill="clear" size="small">
          <ion-icon slot="icon-only" :icon="addCircleOutline"></ion-icon>
        </ion-button>
      </div>

      <div v-for="cat in categories" :key="cat.name" class="category-block">
        <h3 class="cat-title">{{ cat.name }}</h3>
        <ion-list>
          <ion-item
            v-for="item in cat.items"
            :key="item.id"
            @click="toggle(item)"
            button
            detail="false"
            :class="{ done: item.checked }"
          >
            <ion-checkbox
              slot="start"
              :checked="item.checked"
              @ionChange="toggle(item)"
              @click.stop
            ></ion-checkbox>
            <ion-label :class="{ striked: item.checked }">{{ item.name }}</ion-label>
            <ion-button
              slot="end"
              fill="clear"
              color="danger"
              size="small"
              @click.stop="removeItem(item)"
            >
              <ion-icon slot="icon-only" :icon="trashOutline"></ion-icon>
            </ion-button>
          </ion-item>
        </ion-list>
      </div>

      <div class="actions">
        <ion-button fill="clear" color="medium" size="small" @click="clearDone">
          <ion-icon slot="start" :icon="checkmarkDoneOutline"></ion-icon>
          Supprimer cochés
        </ion-button>
      </div>
    </template>
  </Page>
</template>

<script>
import {
  IonButton,
  IonCheckbox,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
} from '@ionic/vue';
import { addCircleOutline, trashOutline, checkmarkDoneOutline } from 'ionicons/icons';
import Page from '../Page.vue';

const STORAGE_KEY = 'grocery_list';

const DEFAULT_ITEMS = [
  { cat: 'PROTÉINES', name: 'Blanc de poulet (1 à 1,2 kg)' },
  { cat: 'PROTÉINES', name: 'Steak haché 5% (600 à 800 g)' },
  { cat: 'PROTÉINES', name: 'Œufs (12 à 18)' },
  { cat: 'PROTÉINES', name: 'Poisson blanc (colin/cabillaud) ou saumon (400 à 600 g)' },
  { cat: 'PROTÉINES', name: 'Skyr ou fromage blanc (1 à 2 kg)' },
  { cat: 'PROTÉINES', name: 'Whey protéine' },
  { cat: 'GLUCIDES', name: 'Riz (500 g cru)' },
  { cat: 'GLUCIDES', name: 'Pommes de terre (1,5 à 2 kg)' },
  { cat: 'GLUCIDES', name: 'Pâtes complètes (500 g)' },
  { cat: 'GLUCIDES', name: 'Bananes' },
  { cat: 'GLUCIDES', name: 'Pommes' },
  { cat: 'LÉGUMES', name: 'Brocolis (surgelés ok)' },
  { cat: 'LÉGUMES', name: 'Courgettes' },
  { cat: 'LÉGUMES', name: 'Carottes' },
  { cat: 'LÉGUMES', name: 'Haricots verts' },
  { cat: 'LÉGUMES', name: 'Salade' },
  { cat: 'LÉGUMES', name: 'Tomates' },
  { cat: 'LIPIDES', name: 'Huile d\'olive' },
  { cat: 'LIPIDES', name: 'Amandes ou noix' },
  { cat: 'LIPIDES', name: 'Avocat (optionnel)' },
];

const CAT_ORDER = ['PROTÉINES', 'GLUCIDES', 'LÉGUMES', 'LIPIDES', 'AUTRE'];

let idSeq = 0;
function makeItem(name, cat = 'AUTRE') {
  return { id: ++idSeq, name, cat, checked: false };
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      parsed.forEach(i => { if (i.id >= idSeq) idSeq = i.id; });
      return parsed;
    }
  } catch (_) {}
  return DEFAULT_ITEMS.map(d => makeItem(d.name, d.cat));
}

export default {
  name: 'GroceryList',
  components: { IonButton, IonCheckbox, IonIcon, IonInput, IonItem, IonLabel, IonList, Page },
  data() {
    return {
      items: load(),
      newItem: '',
      addCircleOutline,
      trashOutline,
      checkmarkDoneOutline,
    };
  },
  computed: {
    categories() {
      const map = {};
      this.items.forEach(item => {
        const c = item.cat || 'AUTRE';
        if (!map[c]) map[c] = [];
        map[c].push(item);
      });
      const result = [];
      CAT_ORDER.forEach(c => {
        if (map[c]) result.push({ name: c, items: map[c] });
      });
      Object.keys(map).forEach(c => {
        if (!CAT_ORDER.includes(c)) result.push({ name: c, items: map[c] });
      });
      return result;
    },
  },
  methods: {
    save() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
    },
    toggle(item) {
      item.checked = !item.checked;
      this.save();
    },
    addItem() {
      const name = this.newItem.trim();
      if (!name) return;
      this.items.push(makeItem(name));
      this.newItem = '';
      this.save();
    },
    removeItem(item) {
      this.items = this.items.filter(i => i.id !== item.id);
      this.save();
    },
    clearDone() {
      this.items = this.items.filter(i => !i.checked);
      this.save();
    },
  },
};
</script>

<style scoped>
.add-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
}
.add-input {
  flex: 1;
  --border-style: solid;
  --border-width: 1px;
  --border-radius: 8px;
  --padding-start: 10px;
}
.cat-title {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ion-color-medium);
  margin: 16px 0 4px 4px;
}
.category-block:first-of-type .cat-title {
  margin-top: 0;
}
.striked {
  text-decoration: line-through;
  opacity: 0.45;
}
.actions {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}
</style>
